import { refreshAccessToken } from "../services/apiAuth";
import translation from "../data/fa-IR.json";

interface ApiClientOptions extends RequestInit {
  isFormData?: boolean;
}

async function makeRequest<T>(
  url: string,
  options: ApiClientOptions
): Promise<T> {
  const storedAuth = localStorage.getItem("auth");
  const accessToken = storedAuth ? JSON.parse(storedAuth).access : null;

  const headers = {
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    ...options.headers,
  };

  if (!options.isFormData) {
    headers["Content-Type"] = "application/json";
  }

  try {
    const res = await fetch(url, { ...options, headers });
    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      if (res.status === 503) {
        throw {
          title: data?.message?.title || `${translation.Global.AppError}`,
          detail:
            data?.message?.detail || `${translation.Global.ServiceUnavailable}`,
          status: res.status,
        };
      } else {
        throw {
          title: data?.message?.title || `${translation.Global.AppError}`,
          detail:
            data?.message?.detail || `${translation.Global.AppErrorDetails}`,
          status: res.status,
        };
      }
    }

    return data as T;
  } catch (err: any) {
    // If fetch itself failed (no response)
    if (err instanceof TypeError || err.name === "AbortError") {
      throw {
        title: translation.Global.ServerError,
        detail: translation.Global.ServerErrorDetail,
        status: 0, // custom "offline" code
      };
    }
    throw err;
  }
}

export async function apiClient<T = any>(
  url: string,
  options: ApiClientOptions = {}
): Promise<T> {
  try {
    return await makeRequest<T>(url, options);
  } catch (error: any) {
    if (error.status === 401) {
      try {
        const newAccess = await refreshAccessToken();
        return await makeRequest<T>(url, {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${newAccess}`,
          },
        });
      } catch (refreshError) {
        throw refreshError;
      }
    }
    throw error;
  }
}
